/**
 * Arabic as the second language (ADR-0025, document 15): helpers shared by the forms and lists
 * that show bilingual record names.
 */

/** True when the text contains at least one Arabic letter. */
export function hasArabicLetter(s: string): boolean {
  return /[\u0621-\u064A\u0671-\u06D3\uFB50-\uFDFF\uFE70-\uFEFC]/.test(s);
}

/** The name to show a reader of `language`: the Arabic one when there is one, else the English. */
export function nameFor(language: 'en' | 'ar', en: string, ar: string | null | undefined): string {
  return language === 'ar' && ar ? ar : en;
}

/** The form value of an optional Arabic field: trimmed, and null when empty (rule AR1). */
export function arabicValue(s: string): string | null {
  const v = s.trim();
  return v === '' ? null : v;
}

/**
 * Hint under an Arabic field: what the field is for, or a warning when it holds no Arabic letter
 * (allowed, for a Latin brand name, but usually a mistake).
 */
export function arabicHint(value: string, what = 'name'): string {
  if (value.trim() && !hasArabicLetter(value))
    return `No Arabic letters: Arabic readers will see this ${what} exactly as typed.`;
  return `Optional. Shown to Arabic readers; the English ${what} is used when this is empty.`;
}

const EASTERN = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN = '۰۱۲۳۴۵۶۷۸۹';

/**
 * Arabic-Indic and Persian digits and the Arabic decimal, thousands and percent signs → their
 * Latin forms (document 15, section 5.2): someone typing on an Arabic keyboard, or pasting from an
 * Arabic document, gets the number they meant.
 */
export function toLatinDigits(s: string): string {
  let out = '';
  for (const ch of s) {
    const e = EASTERN.indexOf(ch);
    const p = PERSIAN.indexOf(ch);
    if (e >= 0) out += String(e);
    else if (p >= 0) out += String(p);
    else if (ch === '\u066B') out += '.';
    else if (ch === '\u066C' || ch === '\u060C') out += ',';
    else if (ch === '\u066A') out += '%';
    else out += ch;
  }
  return out;
}

/**
 * A number, percentage or code kept left to right inside text that may be right to left (LRI …
 * PDI isolates): "-12%" never shows as "12%-" in an Arabic sentence. For JSX, prefer
 * `<bdi dir="ltr">`; this is for strings passed into messages and attributes.
 */
export const ltr = (s: string): string => `\u2066${s}\u2069`;
